import { ArticleLabelType } from 'graphql-ts-types';

import type { ArticleLabel, ArticleLabelsList } from '../types/labels';
import { formatNumberPrice } from './format-price';

const MINIMUM_ABSOLUTE_DISCOUNT_PRICE = 10000; // 100.00 € ~ CHF 100.00

const formatAbsoluteDiscount = (
  intValue: number,
  formatCurrency?: (value: number) => string
): string => {
  if (formatCurrency) {
    const formatted = formatCurrency(intValue);
    // Strip trailing zeros from formatted currency strings (e.g., "50,00 €" → "50 €")
    // Handles both comma and dot as decimal separators

    return formatted.replace(/([,.]0+)(\s|$)/, '$2');
  }

  const numericValue = formatNumberPrice(intValue);
  // Strip trailing decimals for whole numbers

  return numericValue % 1 === 0 ? String(Math.floor(numericValue)) : String(numericValue);
};

export const getLabelStringValue = (
  value: ArticleLabel['value'],
  type: ArticleLabel['type'],
  formatCurrency?: (value: number) => string
): string => {
  if (typeof value !== 'object' || value === null) {
    return '';
  }

  if ('value' in value && typeof value.value === 'string') {
    return value.value;
  }

  if ('intValue' in value && typeof value.intValue === 'number') {
    if (type === ArticleLabelType.PercentageDiscount) {
      return `-${value.intValue}%`;
    }

    if (type === ArticleLabelType.AbsoluteDiscount) {
      return `-${formatAbsoluteDiscount(value.intValue, formatCurrency)}`;
    }

    return `-${value.intValue}`;
  }

  return '';
};

export const getLabelPriority = (type: ArticleLabel['type']): number => {
  switch (type) {
    case ArticleLabelType.Campaign:
      return 0;
    case ArticleLabelType.New:
    case ArticleLabelType.ValuePack:
      return 1;
    case ArticleLabelType.Home24Selection:
      return 2;
    default:
      return 999;
  }
};

export const isSaleLabel = (type: ArticleLabel['type']): boolean => {
  return type === ArticleLabelType.PercentageDiscount || type === ArticleLabelType.AbsoluteDiscount;
};

export const filterSaleLabels = (
  labels_v2: ArticleLabelsList | undefined | null
): ArticleLabelsList => (labels_v2 || []).filter(label => label && isSaleLabel(label.type));

export const filterNonSaleLabels = (
  labels_v2: ArticleLabelsList | undefined | null
): ArticleLabelsList => (labels_v2 || []).filter(label => label && !isSaleLabel(label.type));

export const filterDiscountLabels = (
  labels_v2: ArticleLabelsList | undefined | null,
  regularPrice: number = 0,
  withAbsoluteDiscount: boolean = false
): ArticleLabelsList =>
  (labels_v2 || []).filter(label => {
    if (!label) return true;

    const { type } = label;
    const isPercentage = type === ArticleLabelType.PercentageDiscount;
    const isAbsolute = type === ArticleLabelType.AbsoluteDiscount;

    // Keep all non-discount labels
    if (!isPercentage && !isAbsolute) return true;

    if (!withAbsoluteDiscount) return isPercentage;

    return regularPrice < MINIMUM_ABSOLUTE_DISCOUNT_PRICE ? isPercentage : isAbsolute;
  });
